import { fetchSkill, type SkillBody } from '../api/skill'

/**
 * What the skill panel is looking at: one point, one window, one field.
 *
 * Its own store rather than a corner of `compare`, because the two ask
 * different questions of different data and refetch on different things. The
 * meteogram moves when a *run lands*; this moves when the window changes or
 * the point does, and a run landing changes one bucket out of two dozen — so
 * it deliberately does **not** watch `models.revision`. Refetching 1.3 MB of
 * observations every time a forecast step arrives would be the failure that
 * distinction was built for.
 *
 * The field is shared with the meteogram through `compare.field` where the two
 * agree, so switching the chart to wind switches the grid below it. Where they
 * cannot agree — the chart can draw a field this cannot score — the panel
 * falls back to its own first scoreable field and says so.
 */

export type SkillStatus = 'idle' | 'loading' | 'ready' | 'error'

export const skill = $state({
  body: null as SkillBody | null,
  status: 'idle' as SkillStatus,
  error: null as string | null,
  /** Hours of history asked for. One of `SKILL_WINDOWS`. */
  hours: 24,
  /** The field whose grid is shown, or null to follow the meteogram's. */
  field: null as string | null,
  /** True once a request has 404'd: this deployment keeps no archive. */
  absent: false,
})

export function setSkillWindow(hours: number): void {
  if (skill.hours === hours) return
  skill.hours = hours
}

export function setSkillField(id: string | null): void {
  skill.field = id
}

// Assigning to `$state` stores a proxy, so nothing here may compare bodies by
// identity; staleness is a counter, the same way `compare` tracks it.
let requestSeq = 0
let lastKey = ''

/**
 * Fetch unless this exact question has already been asked.
 *
 * The key is (point, window) and nothing else. It carries no catalog revision
 * on purpose — see the note above.
 */
export async function ensureSkill(lat: number, lon: number, hours = skill.hours): Promise<void> {
  if (skill.absent) return
  const key = `${lat.toFixed(4)},${lon.toFixed(4)}|${hours}`
  if (key === lastKey) return
  lastKey = key

  const seq = ++requestSeq
  skill.status = 'loading'
  skill.error = null
  try {
    const body = await fetchSkill(lat, lon, hours)
    if (seq !== requestSeq) return
    skill.body = body
    skill.status = 'ready'
  } catch (e) {
    if (isAbort(e)) return // Superseded; the newer request owns the state now.
    if (seq !== requestSeq) return
    lastKey = ''
    const message = e instanceof Error ? e.message : String(e)
    if (/keeps no point archive/i.test(message)) {
      // A deployment choice rather than a failure. The panel unmounts itself
      // and stops asking, rather than showing an error for a feature that was
      // switched off on purpose.
      skill.absent = true
      skill.status = 'idle'
      return
    }
    skill.status = 'error'
    skill.error = message
    console.warn('skill fetch failed; keeping the previous table', e)
  }
}

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}

/** Tests only: the module-level dedupe key outlives a component. */
export function _resetSkill(): void {
  lastKey = ''
  requestSeq = 0
  skill.body = null
  skill.status = 'idle'
  skill.error = null
  skill.hours = 24
  skill.field = null
  skill.absent = false
}
